import React from "react";

import Link from "next/link";

import {
  Anchor,
  Badge,
  Button,
  Card,
  Icon,
  Image,
  LineClamp,
  PlayFilledSVG,
  TooltipDialog,
} from "@newsela/angelou";
import dayjs from "dayjs";

import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
  WithTrackEventProps,
} from "@newsela/snitch";

import BookmarkButton from "@/components/BookmarkButton";

import formatHrefToSupportLegacyLink from "@/utils/formatHrefToSupportLegacyLink";
import { BasalCardProps } from "./BasalCard.interface";
import {
  angelouVisuallyHiddenStyles,
  badgeListStyle,
  badgeStyle,
  cardLinkStyle,
  clearAutoMargin,
  displayDateStyle,
  durationVerticalStyle,
  infoIconsStyle,
  footerAreaStyle,
  labelVerticalStyle,
  infoIconsItemStyle,
  imgBotLeftStyle,
  imgBotRightStyle,
  imgOverlayStyles,
  imgTopLeftStyle,
  imgTopRightStyle,
  imageSingleGridStyle,
  imageDoubleGridStyle,
  imageTripleGridStyle,
  imageFourGridStyle,
  imageSectionStyle,
  imageVerticalStyle,
  metaDataStyle,
  textualCss,
  titleAreaStyle,
  titleStyle,
  saveButtonStyle,
  subHeaderStyles,
  subHeaderAreaStyles,
  wrapperStyles,
  verticalCardGridStyles,
} from "./BasalCard.style";
import { durationFormatted } from "./BasalCard.utils";

const DISPLAY_DATE_FORMAT = "MMM DD, YYYY";

type AnchorProps = React.ComponentProps<typeof Anchor> | WithTrackEventProps;
const AnchorWithTrackEvent = withTrackEvent(
  Anchor,
) as React.ForwardRefExoticComponent<
  AnchorProps & React.RefAttributes<HTMLAnchorElement>
>;

const BasalCard = React.forwardRef((props: BasalCardProps, ref) => {
  const {
    badges,
    contentId,
    dataQaSelector,
    displayDate,
    duration,
    footer,
    href,
    images,
    infoIcons,
    label,
    subHeader,
    title,
  } = props;

  const durationObj = duration ? durationFormatted(duration) : null;

  const displayImages = images?.slice(0, 4) || [];
  const displayImagesLength = displayImages.length;
  const getDisplayImageStyle = (imageCount: number) => {
    switch (imageCount) {
      case 1:
        return imageSingleGridStyle;
      case 2:
        return imageDoubleGridStyle;
      case 3:
        return imageTripleGridStyle;
      case 4:
        return imageFourGridStyle;
      default:
        return imageSingleGridStyle;
    }
  };

  return (
    <Card
      __css={[wrapperStyles, verticalCardGridStyles]}
      ref={ref}
      __renderCustomLink={() => (
        <Link
          href={href ? formatHrefToSupportLegacyLink(href) : ""}
          passHref
          legacyBehavior
        >
          <AnchorWithTrackEvent
            __cssFor={{ root: cardLinkStyle }}
            actionName={actionNames.navigate}
            actionPrefix={actionPrefixes.initiate}
            ariaProps={{
              "aria-label": `View "${title || ""}"`,
            }}
            dataProps={{
              ...(dataQaSelector && {
                "data-qa-selector": `${dataQaSelector}-card`,
              }),
            }}
            objectType={objectTypes.content}
            streamProperties={{
              description: "Content card link",
            }}
          />
        </Link>
      )}
    >
      {images && images.length > 0 ? (
        <div
          css={[imageSectionStyle, getDisplayImageStyle(displayImagesLength)]}
        >
          {displayImages.map((image, index) => {
            return (
              <Image
                key={`card-image-${index}`}
                __css={[imageVerticalStyle]}
                alt={image.alt_text || ""}
                src={image.formatted_url || image.url}
                {...image}
              />
            );
          })}
        </div>
      ) : null}
      {badges && badges.length > 0 ? (
        <ul css={[imgTopLeftStyle, badgeListStyle]}>
          {badges?.map((badge, index) => {
            return (
              <Badge
                key={`badge_${index}`}
                flavor={Badge.flavor.solid}
                {...badge}
                size={Badge.size.sm}
                hideIcon
                wrapperTagName="li"
                __cssFor={{ root: badgeStyle }}
              />
            );
          })}
        </ul>
      ) : null}
      {label ? (
        <div css={[imgBotLeftStyle]}>
          <div css={[textualCss, imgOverlayStyles, labelVerticalStyle]}>
            {label}
          </div>
        </div>
      ) : null}
      {durationObj ? (
        <div css={[imgBotRightStyle]}>
          <div css={[durationVerticalStyle, imgOverlayStyles]}>
            <Icon
              isDecorative
              SvgComponent={PlayFilledSVG}
              size={Icon.sizes.sm}
            />
            <span css={angelouVisuallyHiddenStyles}>
              {durationObj.ariaLabel}
            </span>
            <span aria-hidden="true">{durationObj.value}</span>
          </div>
        </div>
      ) : (
        <div />
      )}
      <div css={[metaDataStyle]}>
        {displayDate ? (
          <div css={displayDateStyle}>
            <time dateTime={displayDate}>
              {dayjs(displayDate).format(DISPLAY_DATE_FORMAT)}
            </time>
          </div>
        ) : null}
        {infoIcons && infoIcons.length > 0 ? (
          <ul css={[clearAutoMargin, infoIconsStyle]}>
            {infoIcons?.map((infoIconProps, index) => {
              return (
                <li css={[infoIconsItemStyle]} key={`tooltip_dialog_${index}`}>
                  <TooltipDialog
                    hasShadow
                    body={infoIconProps.body}
                    ariaProps={{ "aria-label": infoIconProps?.buttonLabel }}
                    dialogAriaProps={{
                      "aria-label": infoIconProps?.buttonLabel,
                    }}
                    buttonProps={{
                      flavor: Button.flavors.incognito,
                      size: Button.sizes.sm,
                      icon: {
                        SvgComponent: infoIconProps.SvgComponent,
                      },
                    }}
                  />
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      <div css={[titleAreaStyle]}>
        <LineClamp
          css={[clearAutoMargin, titleStyle]}
          innerElement="h3"
          text={title || ""}
          lines={3}
        />
      </div>
      {subHeader ? (
        <div css={[subHeaderAreaStyles]}>
          <LineClamp css={[subHeaderStyles]} text={subHeader} lines={2} />
        </div>
      ) : null}
      <div css={[footerAreaStyle]}>{footer}</div>
      {contentId ? (
        <div css={[imgTopRightStyle]}>
          <BookmarkButton
            contentId={contentId}
            __cssFor={{ root: saveButtonStyle }}
          />
        </div>
      ) : null}
    </Card>
  );
});

BasalCard.displayName = "BasalCard";
export default BasalCard;
